import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faServer, faHome, faSignInAlt, faUserPlus, faTachometerAlt, faPaperPlane,
  faListAlt, faGamepad, faQrcode, faSignOutAlt, faUserEdit, faExchangeAlt, faCircle
} from '@fortawesome/free-solid-svg-icons';
import { logout as logoutService, validateToken } from '../../services/authService';
import { getBlockchainInfo } from '../../services/walletService';
import LanguageSelector from '../LanguageSelector';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import styles from './Navbar.module.css';

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth, logout: logoutContext } = useContext(AuthContext);

  const [isConnected, setIsConnected] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  // Estado de menú móvil (sincronizado con el checkbox)
  const [mobileOpen, setMobileOpen] = useState(false);
  const togglerRef = useRef(null);
  const profileRef = useRef(null);

  const checkNetworkStatus = useCallback(async () => {
    try {
      const info = await getBlockchainInfo();
      const connected = Boolean(info?.data?.keys ?? true);
      setIsConnected(connected);
    } catch {
      setIsConnected(false);
    }
  }, []);

  const hardCloseMobileMenu = useCallback(() => {
    if (togglerRef.current) togglerRef.current.checked = false;
    setMobileOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    logoutService();
    logoutContext();
    hardCloseMobileMenu();
    navigate('/login');
  }, [logoutContext, navigate, hardCloseMobileMenu]);

  const checkSessionStatus = useCallback(async () => {
    if (!auth?.token || justLoggedIn) return;
    try {
      const { isValid, expired } = await validateToken(auth.token);
      if (!isValid) {
        if (expired) {
          setSessionExpired(true);
          setTimeout(() => handleLogout(), 3000);
        } else {
          handleLogout();
        }
      }
    } catch {
      handleLogout();
    }
  }, [auth?.token, justLoggedIn, handleLogout]);

  // Efectos: red y sesión
  useEffect(() => {
    checkNetworkStatus();
    if (auth?.token) {
      checkSessionStatus();
      setJustLoggedIn(true);
      const tmr = setTimeout(() => setJustLoggedIn(false), 5000);
      return () => clearTimeout(tmr);
    }
  }, [auth?.token, checkNetworkStatus, checkSessionStatus]);

  useEffect(() => {
    const netId = setInterval(checkNetworkStatus, 30000);
    const sesId = setInterval(checkSessionStatus, 60000);
    return () => {
      clearInterval(netId);
      clearInterval(sesId);
    };
  }, [checkNetworkStatus, checkSessionStatus]);

  // Cerrar menú perfil con click-outside / Esc
  useEffect(() => {
    const onDocClick = (e) => {
      if (showProfileMenu && profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setShowProfileMenu(false);
        hardCloseMobileMenu();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showProfileMenu, hardCloseMobileMenu]);

  const toggleProfileMenu = () => setShowProfileMenu((v) => !v);
  const handleEditProfile = () => {
    navigate('/profile');
    setShowProfileMenu(false);
    hardCloseMobileMenu(); // cerrar menú móvil al ir a editar perfil
  };

  // Cerrar menú al clickar cualquier enlace de navegación (delegación)
  const handleLinkClick = () => {
    hardCloseMobileMenu();
    setShowProfileMenu(false);
  };

  // contraen el menú
  const handleLanguageChanged = () => {
    hardCloseMobileMenu();
  };

  return (
    <nav className={styles.nav}>
      <div className={styles.inner}>
        {/* Brand */}
        <Link className={styles.brand} to="/" aria-label="Menelik Home" onClick={handleLinkClick}>
          <img src="/images/logo.webp" alt="Logo" className={styles.logo} />
          <span className={styles.brandText}>{t('navbar.title')}</span>
        </Link>

        {/* Menu toggle (mobile) */}
        <input
          id="nav-toggle"
          type="checkbox"
          ref={togglerRef}
          className={styles.toggler}
          aria-label="Abrir menú"
          onChange={(e) => setMobileOpen(e.target.checked)}
        />
        <label
          htmlFor="nav-toggle"
          className={styles.burger}
          aria-controls="navbarNav"
          aria-expanded={mobileOpen}
        >
          <span />
          <span />
          <span />
        </label>

        {/* Menu links */}
        <div
          id="navbarNav"
          className={`${styles.menu} ${mobileOpen ? styles.menuOpen : ''}`}
        >
          <ul className={styles.links} onClick={handleLinkClick}>
            <li>
              <NavLink to="/" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                <FontAwesomeIcon icon={faHome} /> {t('navbar.home')}
              </NavLink>
            </li>

            {auth?.user ? (
              <>
                <li>
                  <NavLink to="/dashboard" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faTachometerAlt} /> {t('navbar.dashboard')}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/sessions" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faGamepad} /> {t('navbar.games')}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/send" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faPaperPlane} /> {t('navbar.send')}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/transactions" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faListAlt} /> {t('navbar.transactions')}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/receive" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faQrcode} /> {t('navbar.receive')}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/swapfunds" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faExchangeAlt} /> {t('navbar.swapfunds')}
                  </NavLink>
                </li>
               {/* <li>
                  <NavLink to="/smartnodes" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faServer} /> {t('navbar.smartnodes')}
                  </NavLink>
                </li>*/}
                <li>
                  <NavLink to="/fundsdistribution" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faServer} /> {t('navbar.fundsdistribution')}
                  </NavLink>
                </li>
                <li className={styles.logoutLi}>
                  <button className={styles.logoutBtn} onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} /> {t('navbar.logout')}
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <NavLink to="/register" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faUserPlus} /> {t('navbar.register')}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/login" className={({ isActive }) => isActive ? `${styles.link} ${styles.active}` : styles.link}>
                    <FontAwesomeIcon icon={faSignInAlt} /> {t('navbar.login')}
                  </NavLink>
                </li>
              </>
            )}
          </ul>

          {/* Right zone */}
          <div className={styles.rightZone}>
            {/* Estado de red */}
            <div
              className={styles.net}
              title={isConnected ? t('navbar.connected') : t('navbar.disconnected')}
              aria-label={isConnected ? t('navbar.connected') : t('navbar.disconnected')}
            >
              <FontAwesomeIcon icon={faCircle} className={isConnected ? styles.netOk : styles.netBad} />
            </div>

            {/* Perfil */}
            {auth?.user && (
              <div className={styles.profile} ref={profileRef}>
                <button
                  className={styles.profileBtn}
                  onClick={toggleProfileMenu}
                  aria-haspopup="menu"
                  aria-expanded={showProfileMenu}
                >
                  <img
                    src={
                      auth.user.img_url
                        ? `${process.env.REACT_APP_ASSETS_URL}/media/avatars/${auth.user.img_url}`
                        : `${process.env.REACT_APP_ASSETS_URL}/images/avatar.webp`
                    }
                    alt="Avatar"
                    className={styles.avatar}
                  />
                  <span className={styles.nick}>{auth.user.nick}</span>
                </button>

                {showProfileMenu && (
                  <div className={styles.profileMenu} role="menu">
                    <button className={styles.profileItem} onClick={handleEditProfile} role="menuitem">
                      <FontAwesomeIcon icon={faUserEdit} /> {t('navbar.edit_profile')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Selector de idioma */}
            <div className={styles.langWrap} onClick={(e) => e.stopPropagation()}>
              <LanguageSelector onChangeLanguage={handleLanguageChanged} placement="bottom-end" />
            </div>
          </div>
        </div>
      </div>

      {sessionExpired && (
        <div className={styles.sessionToast} role="status" aria-live="polite">
          {t('navbar.session_expired')}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
